import {isFeatureEnabled} from 'feature-flags';
import {Chips, ChipsOption, FormControl, FormField} from 'platform/components';
import {Grid, Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {FieldPathValue, useController} from 'react-hook-form';

import {isEmpty, isNotEmpty, isNotNil} from 'ramda';

import {buildArray, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ELECTRIC_TYPES} from '../../../constants/electricTypes';
import i18n from '../../../i18n/i18n';
import {featureFlags} from '../../../utils/featureFlags';
import {VehicleFormType} from '../types/VehicleFormType';

interface PowerFieldsProps extends TestIdProps {
  power?: number | null;
  fuelType?: string | null;
  powerOptions: ChipsOption[];
  control: FormControl<VehicleFormType>;
  isManualEnterDisabled?: boolean;
  onFilterChange: (
    name: keyof VehicleFormType,
    value: FieldPathValue<VehicleFormType, keyof VehicleFormType>
  ) => void;
  onPowerFieldChange: () => void;
}

export function PowerFields(props: PowerFieldsProps) {
  const {fieldState} = useController({name: 'power', control: props.control});
  const error = fieldState.error;
  const errorMessage = error?.message;
  const isInvalid = !!error;

  const shouldShowMaxPowerInput =
    isNotNil(props.fuelType) &&
    ELECTRIC_TYPES.includes(props.fuelType) &&
    isFeatureEnabled(featureFlags.PR_SPLIT_POWER);

  const powerOptionsWithOther = buildArray(props.powerOptions).whenNot(
    props.isManualEnterDisabled,
    {
      value: '',
      label: i18n.t('page.create.labels.other'),
    }
  );
  const isPowerInOptions = props.powerOptions.some(
    (option) => option.value === props.power?.toString()
  );
  const shouldShowPowerInput =
    !props.isManualEnterDisabled &&
    (isEmpty(props.powerOptions) || (!isPowerInOptions && props.power !== undefined));
  const powerChipValue = match([props.power, isPowerInOptions])
    .with([Pattern.number, true], () => [props.power?.toString() ?? ''])
    .with([Pattern.number, false], () => [''])
    .with([null, Pattern.any], () => [''])
    .otherwise(() => []);

  const onPowerChipChange = (value: string[] | Nullish) => {
    match(value)
      .with([''], () => props.onFilterChange('power', null))
      .with([Pattern.string], ([value]) => props.onFilterChange('power', Number(value)))
      .otherwise(() => props.onFilterChange('power', undefined));
    props.onPowerFieldChange?.();
  };

  return (
    <>
      <Show when={isNotEmpty(props.powerOptions) || props.isManualEnterDisabled}>
        <Chips
          name="power"
          value={powerChipValue}
          onChange={onPowerChipChange}
          label={i18n.t('page.create.labels.power')}
          options={powerOptionsWithOther}
          errorMessage={errorMessage}
          isInvalid={isInvalid}
          isRequired
          isDeselectable={powerOptionsWithOther.length > 1}
          data-testid={suffixTestId('power', props)}
        />
      </Show>
      <Show when={shouldShowPowerInput || shouldShowMaxPowerInput}>
        <Grid columns={[1, 1, 2]} spacing={6}>
          <Show when={shouldShowPowerInput}>
            <FormField
              control={props.control}
              type="number"
              name="power"
              label={`${i18n.t('page.create.labels.power')} (${i18n.t('general.metric.kW')})`}
              tooltip={
                isFeatureEnabled(featureFlags.PR_SPLIT_POWER)
                  ? i18n.t('page.create.labels.powerTooltip')
                  : undefined
              }
              isRequired
              data-testid={suffixTestId('power', props)}
            />
          </Show>
          <Show when={shouldShowMaxPowerInput}>
            <FormField
              control={props.control}
              type="number"
              name="maxPower"
              label={`${i18n.t('page.create.labels.maxPower')} (${i18n.t('general.metric.kW')})`}
              tooltip={i18n.t('page.create.labels.maxPowerTooltip')}
              data-testid={suffixTestId('maxPower', props)}
            />
          </Show>
        </Grid>
      </Show>
    </>
  );
}
